import { useEffect, useRef } from 'react';

export default function CustomCursor() {
  const cursorRef = useRef(null);
  const pos = useRef({ x: -100, y: -100 });
  const target = useRef({ x: -100, y: -100 });
  const rafRef = useRef(null);
  const stateRef = useRef('default'); // default | hovering | clicking

  useEffect(() => {
    // Hide system cursor on landing page
    document.body.style.cursor = 'none';

    const lerp = (a, b, t) => a + (b - a) * t;

    const onMove = (e) => {
      target.current = { x: e.clientX, y: e.clientY };
    };

    const onDown = () => {
      stateRef.current = 'clicking';
      cursorRef.current?.classList.add('cursor-click');
      cursorRef.current?.classList.remove('cursor-hover');
    };

    const onUp = () => {
      stateRef.current = 'default';
      cursorRef.current?.classList.remove('cursor-click');
    };

    // Add hover detection to all interactive elements
    const interactives = document.querySelectorAll('a, button, [role="button"], input, textarea, select, label');
    const onEnter = () => {
      stateRef.current = 'hovering';
      cursorRef.current?.classList.add('cursor-hover');
      cursorRef.current?.classList.remove('cursor-click');
    };
    const onLeave = () => {
      stateRef.current = 'default';
      cursorRef.current?.classList.remove('cursor-hover');
    };

    interactives.forEach(el => {
      el.addEventListener('mouseenter', onEnter);
      el.addEventListener('mouseleave', onLeave);
    });

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);

    const render = () => {
      pos.current.x = lerp(pos.current.x, target.current.x, 0.12);
      pos.current.y = lerp(pos.current.y, target.current.y, 0.12);
      if (cursorRef.current) {
        cursorRef.current.style.left = `${pos.current.x}px`;
        cursorRef.current.style.top  = `${pos.current.y}px`;
      }
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    return () => {
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      interactives.forEach(el => {
        el.removeEventListener('mouseenter', onEnter);
        el.removeEventListener('mouseleave', onLeave);
      });
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <>
      <style>{`
        .custom-cursor {
          position: fixed;
          width: 18px;
          height: 18px;
          border: 2.5px solid #0F172A;
          border-radius: 50%;
          pointer-events: none;
          z-index: 99999;
          transform: translate(-50%, -50%);
          transition: width 0.15s ease, height 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
          will-change: left, top;
          mix-blend-mode: multiply;
        }
        .custom-cursor::after {
          content: '';
          position: absolute;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: 4px; height: 4px;
          background: #0F172A;
          border-radius: 50%;
          transition: all 0.15s ease;
        }
        .custom-cursor.cursor-hover {
          width: 44px;
          height: 44px;
          background-color: rgba(232, 67, 10, 0.12);
          border-color: #E8430A;
        }
        .custom-cursor.cursor-hover::after {
          background: #E8430A;
          width: 5px; height: 5px;
        }
        .custom-cursor.cursor-click {
          width: 12px;
          height: 12px;
          background-color: rgba(232, 67, 10, 0.4);
          border-color: #E8430A;
        }
        @media (hover: none) {
          .custom-cursor { display: none; }
        }
      `}</style>
      <div ref={cursorRef} className="custom-cursor" aria-hidden="true" />
    </>
  );
}
